import i18n from '../i18n';

/**
 * Escape HTML special characters to prevent XSS in raw HTML contexts
 * (e.g., Google Maps InfoWindow content strings).
 */
export const escapeHtml = (str) => {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

// Format tag for display: replace underscores with spaces and convert to Title Case
export const formatTagDisplay = (tag) => {
  return tag
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

/**
 * Join a list of already-translated fragments the way the ACTIVE LANGUAGE
 * joins lists — "A, B, and C" (en) vs "A, B en C" (nl). Uses the platform's
 * Intl.ListFormat rather than hand-assembling separators from catalog keys,
 * because separator placement (and whether a serial comma appears at all)
 * is a property of the language, not copy we should be authoring.
 *
 * @param {string[]} items - already-localized fragments
 * @param {'conjunction'|'disjunction'} [type] - "and" vs "or" joining
 */
export const formatList = (items, type = 'conjunction') => {
  const list = (items || []).filter(Boolean).map(String);
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  try {
    return new Intl.ListFormat(i18n.language, { style: 'long', type }).format(list);
  } catch (e) {
    // Very old engine, or an unknown language tag — comma-join is a safe,
    // universally-parseable degradation.
    return list.join(', ');
  }
};

/**
 * Age groups considered as children for filtering purposes
 */
export const CHILD_AGE_GROUPS = ['toddler', 'preschool', 'elementary', 'teen'];

/**
 * Check if group members include any children
 * @param {Array} groupMembers - Array of group member objects with type/ageGroup properties
 * @returns {boolean} True if group includes children
 */
export const hasChildren = (groupMembers) => {
  if (!groupMembers || groupMembers.length === 0) return false;
  return groupMembers.some(member =>
    CHILD_AGE_GROUPS.includes(member.ageGroup) || member.type === 'child'
  );
};

/**
 * Format group members for display (e.g., "2 Adults, 1 Child")
 * @param {Array} groupMembers - Array of group member objects with type/ageGroup properties
 * @returns {string} Formatted display string
 */
export const formatGroupMembersDisplay = (groupMembers) => {
  if (!groupMembers || groupMembers.length === 0) return i18n.t('labels:groupDisplay.justMe');

  const childAgeGroups = CHILD_AGE_GROUPS;

  const adultCount = groupMembers.filter(m =>
    m.type === 'adult' || m.ageGroup === 'adult'
  ).length;

  const childCount = groupMembers.filter(m =>
    m.type === 'child' || childAgeGroups.includes(m.ageGroup)
  ).length;

  // Locale-aware list join of already-translated count fragments.
  const parts = [];
  if (adultCount > 0) parts.push(i18n.t('labels:groupDisplay.adults', { count: adultCount }));
  if (childCount > 0) parts.push(i18n.t('labels:groupDisplay.children', { count: childCount }));

  return parts.length > 0 ? formatList(parts) : i18n.t('labels:groupDisplay.justMe');
};
